import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Artplayer from 'artplayer';
import { Spin, message } from 'antd';
import { IPC, type DownloadRemoteFileResponse } from '../../../../common/ipc/channels';
import playDownloadIcon from '../../../assets/images/play-download.svg';
import './VideoPlayer.css';

interface VideoPlayerSource {
  /** 视频地址 */
  url: string;
  /** 视频标题 */
  label: string;
}

interface VideoPlayerProps {
  /** 视频资源列表 */
  sources: VideoPlayerSource[];
  /** 是否自动播放 */
  autoPlay?: boolean;
  /** 是否根据视频源动态调整画面比例 */
  adaptiveAspectRatio?: boolean;
  /** 是否展示画面左上角标题 */
  showHeader?: boolean;
  /** 是否展示选集入口；默认仅在多视频源时展示 */
  showSourceSelector?: boolean;
  /** 是否展示下载入口 */
  showDownload?: boolean;
  /** 是否启用沉浸式毛玻璃展示 */
  immersive?: boolean;
  /** 自定义 className */
  className?: string;
  /** 快进秒数，默认 10s */
  forwardStep?: number;
}

type VideoAspectMode = 'landscape' | 'portrait' | 'square';

function resolveVideoAspectMeta(videoWidth: number, videoHeight: number) {
  if (videoWidth <= 0 || videoHeight <= 0) {
    return {
      ratio: undefined,
      mode: null,
    } as const;
  }

  const ratio = videoWidth / videoHeight;

  // 用较小的容差吸收编码后的像素偏差，避免接近 1:1 的视频来回抖动。
  if (Math.abs(ratio - 1) < 0.05) {
    return {
      ratio,
      mode: 'square' as const,
    };
  }

  return {
    ratio,
    mode: (ratio > 1 ? 'landscape' : 'portrait') as VideoAspectMode,
  };
}

function buildSafeVideoFileName(label?: string) {
  const safeLabel = (label || 'creative-writer-video').replace(/[\\/:*?"<>|]+/g, '-');
  return `${safeLabel}.mp4`;
}

function escapeHtml(value: string) {
  return value.replace(/[&<>"']/g, (char) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  }[char] || char));
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  sources,
  autoPlay = false,
  adaptiveAspectRatio = false,
  showHeader = true,
  showSourceSelector,
  showDownload = false,
  immersive = false,
  className,
  forwardStep = 10,
}) => {
  const artContainerRef = useRef<HTMLDivElement>(null);
  const artRef = useRef<Artplayer | null>(null);
  const availableSources = useMemo(
    () => sources
      .filter((item) => item.url.trim())
      .map((item) => ({ ...item, label: item.label.trim() || '视频' })),
    [sources]
  );
  const [activeSourceIndex, setActiveSourceIndex] = useState(0);
  const [videoAspectRatio, setVideoAspectRatio] = useState<number | undefined>(undefined);
  const [videoAspectMode, setVideoAspectMode] = useState<VideoAspectMode | null>(null);
  const [isMetaPending, setIsMetaPending] = useState(false);
  const isDownloadingRef = useRef(false);
  const activeSource = availableSources[activeSourceIndex] ?? null;
  const shouldShowSourceSelector = showSourceSelector ?? availableSources.length > 1;
  const isAdaptiveMetaPending = Boolean(immersive && adaptiveAspectRatio && activeSource && isMetaPending);

  useEffect(() => {
    if (activeSourceIndex < availableSources.length) {
      return;
    }

    setActiveSourceIndex(0);
  }, [activeSourceIndex, availableSources.length]);

  useEffect(() => {
    setVideoAspectRatio(undefined);
    setVideoAspectMode(null);
    setIsMetaPending(Boolean(adaptiveAspectRatio && activeSource));
  }, [activeSource, adaptiveAspectRatio]);

  const handleDownload = useCallback(async () => {
    if (!activeSource?.url) {
      return;
    }

    if (isDownloadingRef.current) {
      return;
    }

    isDownloadingRef.current = true;

    try {
      const fileName = buildSafeVideoFileName(activeSource.label);

      // Electron 主进程下载不受渲染进程 CORS 限制，优先走 IPC。
      if (window.electronAPI) {
        const result = await window.electronAPI.invoke(IPC.DOWNLOAD_REMOTE_FILE, {
          url: activeSource.url,
          fileName,
        }) as DownloadRemoteFileResponse;

        if (result.success) {
          message.success('视频下载成功');
          return;
        }

        if (result.canceled) {
          return;
        }

        throw new Error(result.error || '视频下载失败');
      }

      const anchor = document.createElement('a');
      anchor.href = activeSource.url;
      anchor.download = fileName;
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      message.success('已开始下载视频');
    } catch (error) {
      message.error(error instanceof Error ? error.message : '视频下载失败');
    } finally {
      isDownloadingRef.current = false;
    }
  }, [activeSource]);

  useEffect(() => {
    const container = artContainerRef.current;
    if (!container || !activeSource) {
      artRef.current?.destroy(true);
      artRef.current = null;
      return undefined;
    }

    const controls = [];

    if (shouldShowSourceSelector) {
      controls.push({
        name: 'sourceSelector',
        position: 'right' as const,
        html: '选集',
        tooltip: activeSource.label,
        selector: availableSources.map((item, index) => ({
          html: escapeHtml(item.label),
          value: index,
          default: index === activeSourceIndex,
        })),
        onSelect: (_item: unknown) => {
          const nextIndex = Number((_item as { value?: number | string }).value);
          if (!Number.isNaN(nextIndex)) {
            setActiveSourceIndex(nextIndex);
          }
        },
      });
    }

    if (showDownload) {
      controls.push({
        name: 'download',
        position: 'right' as const,
        html: `<img src="${playDownloadIcon}" alt="" class="video-player__art-download-icon" />`,
        tooltip: '下载',
        click: () => {
          void handleDownload();
        },
      });
    }

    artRef.current?.destroy(true);
    Artplayer.FAST_FORWARD_VALUE = forwardStep;

    const art = new Artplayer({
      container,
      url: activeSource.url,
      autoplay: autoPlay,
      volume: 1,
      lang: 'zh-cn',
      theme: '#009dff',
      hotkey: true,
      mutex: true,
      playbackRate: true,
      setting: true,
      pip: false,
      fullscreen: false,
      fullscreenWeb: false,
      miniProgressBar: true,
      playsInline: true,
      fastForward: true,
      backdrop: immersive,
      controls,
      moreVideoAttr: {
        preload: 'metadata',
        playsInline: true,
      },
    });

    const syncVideoMeta = () => {
      const aspectMeta = resolveVideoAspectMeta(art.video.videoWidth, art.video.videoHeight);
      if (!aspectMeta.ratio) {
        return;
      }

      setVideoAspectRatio(aspectMeta.ratio);
      setVideoAspectMode(aspectMeta.mode);
      setIsMetaPending(false);
    };

    const finishMetaPending = () => {
      setIsMetaPending(false);
    };

    art.on('ready', syncVideoMeta);
    art.on('video:loadedmetadata', syncVideoMeta);
    art.on('video:error', finishMetaPending);
    art.video.addEventListener('loadedmetadata', syncVideoMeta);
    art.video.addEventListener('loadeddata', syncVideoMeta);
    art.video.addEventListener('canplay', finishMetaPending);
    art.video.addEventListener('error', finishMetaPending);
    const syncMetaFrame = window.requestAnimationFrame(syncVideoMeta);
    artRef.current = art;

    return () => {
      window.cancelAnimationFrame(syncMetaFrame);
      art.video.removeEventListener('loadedmetadata', syncVideoMeta);
      art.video.removeEventListener('loadeddata', syncVideoMeta);
      art.video.removeEventListener('canplay', finishMetaPending);
      art.video.removeEventListener('error', finishMetaPending);
      art.destroy(true);
      if (artRef.current === art) {
        artRef.current = null;
      }
    };
  }, [
    activeSource,
    activeSourceIndex,
    autoPlay,
    availableSources,
    forwardStep,
    handleDownload,
    immersive,
    shouldShowSourceSelector,
    showDownload,
  ]);

  const rootClassName = [
    'video-player',
    activeSource ? 'video-player--has-source' : '',
    adaptiveAspectRatio && activeSource ? 'video-player--adaptive' : '',
    adaptiveAspectRatio && videoAspectMode ? `video-player--${videoAspectMode}` : '',
    immersive ? 'video-player--immersive' : '',
    className || '',
  ]
    .filter(Boolean)
    .join(' ');

  const aspectRatioStyle = videoAspectRatio && adaptiveAspectRatio
    ? ({ '--video-player-aspect-ratio': `${videoAspectRatio}` } as React.CSSProperties)
    : undefined;

  return (
    <div className={rootClassName} style={aspectRatioStyle}>
      <div className="video-player__surface">
        {immersive ? <div className="video-player__backdrop" /> : null}
        {showHeader ? (
          <div className="video-player__header">
            <span className="video-player__title">{activeSource?.label || '使用说明'}</span>
          </div>
        ) : null}
        <div className="video-player__media-frame" style={aspectRatioStyle}>
          {activeSource ? (
            <>
              <div
                ref={artContainerRef}
                className={`video-player__art${isAdaptiveMetaPending ? ' video-player__art--pending' : ''}`}
              />
              {isAdaptiveMetaPending ? (
                <div className="video-player__loading">
                  <Spin size="large" />
                </div>
              ) : null}
            </>
          ) : (
            <div className="video-player__empty">暂无可播放视频</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;
